import { useEffect, useState } from "react";
import { MobileLayout } from "../components/layout/MobileLayout";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../components/ui/Avatar.jsx";
import { Button } from "../components/ui/Button.jsx";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/Card.jsx";
import { Input } from "../components/ui/Input.jsx";
import { Label } from "../components/ui/Label.jsx";
import { useToast } from "../hooks/useToast.js";
import {
  getCurrentUser,
  logout,
  updateUserProfile,
} from "../services/authService.js";
import { getCompany, updateCompany } from "../services/companyService.js";
import { EditCompanyModal } from "./EditCompanyModal.jsx";
import { EditUserModal } from "./EditUserModal.jsx";
import { ArrowLeft, Building, UserRoundPen } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Profile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    id: "",
    name: "",
    user_role: "",
    email: "",
    contact_number: "",
    created_at: "",
  });
  const [company, setCompany] = useState(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [userForm, setUserForm] = useState({});
  const [companyForm, setCompanyForm] = useState({});
  const { toast } = useToast();

  const loadData = async () => {
    try {
      const res = await getCurrentUser();
      if (res?.success && res.user) {
        setProfile({
          id: res.user.id,
          name: res.user.name || "",
          user_role: res.user.user_role || "",
          email: res.user.email || "",
          contact_number: res.user.contact_number || "",
          created_at: res.user.created_at || "",
        });
      }
      const companyData = await getCompany();
      setCompany(companyData);
    } catch (err) {
       toast({
        variant: "destructive",
        title: "Error",
        description: "Profile load error:",
      });
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleLogout = async () => {
    try {
      const res = await logout();
      if (res?.success) {
        toast({
          title: "Logged Out",
          description: "You have been successfully logged out.",
        });
        window.location.href = "/";
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "Logout failed",
        variant: "destructive",
      });
    }
  };

  const openUserEdit = () => {
    setUserForm({
      name: profile.name,
      email: profile.email,
      contact_number: profile.contact_number,
    });
    setIsUserModalOpen(true);
  };

  const openCompanyEdit = () => {
    if (!company) return;
    setCompanyForm({
      company_name: company.company_name || "",
      company_email: company.company_email || "",
      company_phone: company.company_phone || "",
      company_address: company.company_address || "",
    });
    setIsCompanyModalOpen(true);
  };

  return (
    <MobileLayout userRole={profile.user_role}>
      <div className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-full"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-bold">Profile</h1>
        </div>

        <Card className="bg-transparent border-none shadow-none">
          <CardContent className="flex gap-2 my-4  transition-shadow bg-white border-2 shadow-sm cursor-pointer text-start border-blue-100/50 rounded-xl">
            <div className="relative inline-block">
              <Avatar className="w-16 h-16">
                <AvatarImage
                  src="/placeholder.svg"
                  alt={profile.name || "User"}
                  className="!rounded-lg"
                />
                <AvatarFallback className="text-2xl font-bold !rounded-lg">
                  {(profile.name || "U")
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </div>

            <div>
              <h2 className="text-lg font-bold leading-5 capitalize">
                {profile.name || "—"}
              </h2>
              <p className="my-1 text-xs text-muted-foreground">
                {profile.user_role === "business_admin"
                  ? "Admin"
                  : "Sales Representative"}
              </p>
              <p className="text-xs text-muted-foreground">
                Joined{" "}
                {profile.created_at
                  ? new Date(profile.created_at).toLocaleString("en-IN", {
                      timeZone: "Asia/Kolkata",
                      year: "numeric",
                      month: "short",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      hour12: true, // optional, shows AM/PM
                    })
                  : "—"}
              </p>
            </div>
          </CardContent>

          <CardContent className="pb-0 space-y-1 transition-shadow bg-white border-2 shadow-sm cursor-pointer text-start border-blue-100/50 rounded-xl">
            <h2 className="font-bold">Personal Information</h2>
            <div>
              <Label>Name</Label>
              <Input value={profile.name} disabled className="h-8 text-xs" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={profile.email} disabled className="h-8 text-xs" />
            </div>
            <div>
              <Label>Phone Number</Label>
              <Input
                value={profile.contact_number}
                disabled
                className="h-8 text-xs"
              />
            </div>

            <h2 className="mt-4 font-bold">Company Details</h2>
            <div>
              <Label>Company Name</Label>
              <Input
                value={company?.company_name || "—"}
                className="h-8 text-xs"
                disabled
              />
            </div>
            <div>
              <Label>Company Email</Label>
              <Input
                value={company?.company_email || "—"}
                className="h-8 text-xs"
                disabled
              />
            </div>
            <div>
              <Label>Company Address</Label>
              <Input
                value={company?.company_address || "—"}
                className="h-8 text-xs"
                disabled
              />
            </div>
            <div>
              <Label>Company Contact Number</Label>
              <Input
                value={company?.company_phone || "—"}
                className="h-8 mb-2 text-xs"
                disabled
              />
            </div>
          </CardContent>
        </Card>

        
        <div className="flex gap-2 w-full">
          {profile.user_role === "business_admin" && (
            <>
              <Button
                className="flex-1 h-8 text-[10px] border rounded-full bg-primary/90 border-primary text-white flex items-center justify-center gap-2"
                onClick={openUserEdit}
              >
                <UserRoundPen size={10} /> Edit User
              </Button>
              <Button
                className="flex-1 h-8  text-[10px] text-white border border-green-600 rounded-full bg-green-700/90 flex items-center justify-center gap-2"
                onClick={openCompanyEdit}
              >
                <Building size={10} /> Edit Company
              </Button>
            </>
          )}

        </div>

        <EditUserModal
          isOpen={isUserModalOpen}
          setIsOpen={setIsUserModalOpen}
          user={profile}
          onUserUpdated={async (updatedData) => {
            await updateUserProfile(profile.id, updatedData);
            loadData();
          }}
        />

        <EditCompanyModal
          isOpen={isCompanyModalOpen}
          setIsOpen={setIsCompanyModalOpen}
          company={company}
          onCompanyUpdated={async (updatedData) => {
            await updateCompany(company.id, updatedData);
            loadData();
          }}
        />
      </div>
    </MobileLayout>
  );
}
